/**
 * The teacher portal's sample roster — the fixtures only.
 *
 * A teacher who opens the portal on a day nothing meets used to get "No programs meet
 * today." and a blank screen, which says nothing about what the portal does. Instead the
 * same screens are rendered against a made-up class so the tool can be seen, learned and
 * demonstrated on any day of the year.
 *
 * The cast is deliberately fictional-famous (Harry Potter) so nobody can mistake it for a
 * real family, and it is paired with a SAMPLE watermark on every screen that shows it.
 *
 * Everything here is invented in the browser: no id in this file exists in the database,
 * and nothing derived from it is ever written back (see `sampleStore.ts` and the
 * `isSampleId` guards in the attendance hooks).
 */

import { shiftDateString } from '../../utils/schedule';
import type { AttendanceStatus } from '../../types/attendance';
import type { TodayProgram } from '../useTodaysPrograms';

/**
 * Every sample id starts with this. Real ids are UUIDs, so the prefix can never collide —
 * which is what lets a single `isSampleId` check divert an entire query or mutation away
 * from Supabase.
 */
export const SAMPLE_ID_PREFIX = 'sample-';

export function isSampleId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(SAMPLE_ID_PREFIX);
}

export const SAMPLE_PROGRAM_IDS = {
  afterSchool: 'sample-program-after-school',
  potions: 'sample-program-potions',
} as const;

interface SampleGuardian {
  name: string;
  relationship: string;
}

/** How a student's day starts when the sample is (re)seeded. */
interface SampleSeed {
  status: AttendanceStatus;
  /** Minutes before "now" the check-in happened, when there is one. */
  checkedInMinutesAgo?: number;
  checkedInByName?: string;
  /** Minutes before "now" the check-out happened. */
  pickedUpMinutesAgo?: number;
  pickedUpByName?: string;
  pickupIsAuthorized?: boolean;
  pickupRecordedByName?: string;
  absenceReason?: string;
}

export interface SampleStudent {
  enrollmentId: string;
  studentId: string;
  recordId: string;
  programId: string;
  firstName: string;
  lastName: string;
  grade: string;
  school: string;
  roomNumber: string | null;
  hasAllergies: boolean;
  allergiesDetails: string | null;
  viaSummerPass: boolean;
  /**
   * Day offsets from today for a `per_day` program, materialized into dates at read time
   * so the chips always read as this week rather than a frozen date in the fixtures.
   */
  selectedDayOffsets: number[] | null;
  /** Rendered by the check-out sheet as the always-authorized parent/guardian list. */
  guardians: SampleGuardian[];
  /** The extra people a parent has pre-approved, shown below the guardians. */
  authorizedPickups: SampleGuardian[];
  seed: SampleSeed;
}

/**
 * Three schools, named so they sort the way the demo wants to be read: the roster selects
 * the alphabetically first school by default, and that should be the big class, not a
 * two-student outlier.
 */
const HOGWARTS = 'Hogwarts Elementary School';
const ILVERMORNY = 'Ilvermorny Academy';
const UAGADOU = 'Uagadou Institute';

const WEASLEY_PARENTS: SampleGuardian[] = [
  { name: 'Molly Weasley', relationship: 'Mother' },
  { name: 'Arthur Weasley', relationship: 'Father' },
];

/**
 * The sample class list.
 *
 * Shaped to exercise the parts of the roster a teacher actually relies on: three schools
 * (so the school tabs appear), several grades per school, rooms on most students but not
 * all (so both the room markers and the "No room" cluster show), an allergy flag, a summer
 * pass, and a day that is already part-way recorded.
 */
export const SAMPLE_STUDENTS: readonly SampleStudent[] = [
  {
    enrollmentId: 'sample-enr-harry', studentId: 'sample-stu-harry', recordId: 'sample-rec-harry',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Harry', lastName: 'Potter', grade: '3', school: HOGWARTS, roomNumber: '12',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: [
      { name: 'Petunia Dursley', relationship: 'Aunt' },
      { name: 'Vernon Dursley', relationship: 'Uncle' },
    ],
    authorizedPickups: [
      { name: 'Sirius Black', relationship: 'Godfather' },
      { name: 'Rubeus Hagrid', relationship: 'Family friend' },
    ],
    seed: { status: 'checked_in', checkedInMinutesAgo: 26, checkedInByName: 'Minerva McGonagall' },
  },
  {
    enrollmentId: 'sample-enr-hermione', studentId: 'sample-stu-hermione', recordId: 'sample-rec-hermione',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Hermione', lastName: 'Granger', grade: '3', school: HOGWARTS, roomNumber: '12',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: [
      { name: 'Jean Granger', relationship: 'Mother' },
      { name: 'Wendell Granger', relationship: 'Father' },
    ],
    authorizedPickups: [],
    seed: { status: 'checked_in', checkedInMinutesAgo: 24, checkedInByName: 'Minerva McGonagall' },
  },
  {
    enrollmentId: 'sample-enr-ron', studentId: 'sample-stu-ron', recordId: 'sample-rec-ron',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Ron', lastName: 'Weasley', grade: '3', school: HOGWARTS, roomNumber: '14',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: WEASLEY_PARENTS, authorizedPickups: [{ name: 'Bill Weasley', relationship: 'Brother' }],
    seed: { status: 'absent', absenceReason: 'Sick' },
  },
  {
    enrollmentId: 'sample-enr-neville', studentId: 'sample-stu-neville', recordId: 'sample-rec-neville',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Neville', lastName: 'Longbottom', grade: '2', school: HOGWARTS, roomNumber: '8',
    hasAllergies: true, allergiesDetails: 'Peanuts', viaSummerPass: false, selectedDayOffsets: null,
    guardians: [{ name: 'Augusta Longbottom', relationship: 'Grandmother' }],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-luna', studentId: 'sample-stu-luna', recordId: 'sample-rec-luna',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Luna', lastName: 'Lovegood', grade: '2', school: HOGWARTS, roomNumber: '8',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: [{ name: 'Xenophilius Lovegood', relationship: 'Father' }],
    authorizedPickups: [],
    seed: {
      status: 'picked_up',
      checkedInMinutesAgo: 28, checkedInByName: 'Minerva McGonagall',
      pickedUpMinutesAgo: 4, pickedUpByName: 'Xenophilius Lovegood',
      pickupIsAuthorized: true, pickupRecordedByName: 'Minerva McGonagall',
    },
  },
  {
    enrollmentId: 'sample-enr-ginny', studentId: 'sample-stu-ginny', recordId: 'sample-rec-ginny',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Ginny', lastName: 'Weasley', grade: '1', school: HOGWARTS, roomNumber: '4',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: WEASLEY_PARENTS, authorizedPickups: [{ name: 'Bill Weasley', relationship: 'Brother' }],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-draco', studentId: 'sample-stu-draco', recordId: 'sample-rec-draco',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Draco', lastName: 'Malfoy', grade: '4', school: HOGWARTS, roomNumber: '20',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: [
      { name: 'Narcissa Malfoy', relationship: 'Mother' },
      { name: 'Lucius Malfoy', relationship: 'Father' },
    ],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-cho', studentId: 'sample-stu-cho', recordId: 'sample-rec-cho',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Cho', lastName: 'Chang', grade: '4', school: HOGWARTS, roomNumber: null,
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: [{ name: 'Ming Chang', relationship: 'Mother' }],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-newt', studentId: 'sample-stu-newt', recordId: 'sample-rec-newt',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Newt', lastName: 'Scamander', grade: '5', school: ILVERMORNY, roomNumber: '3',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: [{ name: 'Theseus Scamander', relationship: 'Brother' }],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-tina', studentId: 'sample-stu-tina', recordId: 'sample-rec-tina',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Tina', lastName: 'Goldstein', grade: '1', school: ILVERMORNY, roomNumber: null,
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: null,
    guardians: [{ name: 'Queenie Goldstein', relationship: 'Sister' }],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-viktor', studentId: 'sample-stu-viktor', recordId: 'sample-rec-viktor',
    programId: SAMPLE_PROGRAM_IDS.afterSchool,
    firstName: 'Viktor', lastName: 'Krum', grade: '5', school: UAGADOU, roomNumber: '1',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: true, selectedDayOffsets: null,
    guardians: [{ name: 'Ivan Krum', relationship: 'Father' }],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },

  // ── Potions Club: a per-day program, so these rows carry selected days ──────────
  {
    enrollmentId: 'sample-enr-hermione-potions', studentId: 'sample-stu-hermione', recordId: 'sample-rec-hermione-potions',
    programId: SAMPLE_PROGRAM_IDS.potions,
    firstName: 'Hermione', lastName: 'Granger', grade: '3', school: HOGWARTS, roomNumber: '12',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: [0, 2, 7],
    guardians: [
      { name: 'Jean Granger', relationship: 'Mother' },
      { name: 'Wendell Granger', relationship: 'Father' },
    ],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-neville-potions', studentId: 'sample-stu-neville', recordId: 'sample-rec-neville-potions',
    programId: SAMPLE_PROGRAM_IDS.potions,
    firstName: 'Neville', lastName: 'Longbottom', grade: '2', school: HOGWARTS, roomNumber: '8',
    hasAllergies: true, allergiesDetails: 'Peanuts', viaSummerPass: false, selectedDayOffsets: [0, 7],
    guardians: [{ name: 'Augusta Longbottom', relationship: 'Grandmother' }],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
  {
    enrollmentId: 'sample-enr-draco-potions', studentId: 'sample-stu-draco', recordId: 'sample-rec-draco-potions',
    programId: SAMPLE_PROGRAM_IDS.potions,
    firstName: 'Draco', lastName: 'Malfoy', grade: '4', school: HOGWARTS, roomNumber: '20',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: [0, 2],
    guardians: [
      { name: 'Narcissa Malfoy', relationship: 'Mother' },
      { name: 'Lucius Malfoy', relationship: 'Father' },
    ],
    authorizedPickups: [],
    seed: { status: 'checked_in', checkedInMinutesAgo: 12, checkedInByName: 'Severus Snape' },
  },
  {
    enrollmentId: 'sample-enr-cedric-potions', studentId: 'sample-stu-cedric', recordId: 'sample-rec-cedric-potions',
    programId: SAMPLE_PROGRAM_IDS.potions,
    firstName: 'Cedric', lastName: 'Diggory', grade: '5', school: HOGWARTS, roomNumber: '9',
    hasAllergies: false, allergiesDetails: null, viaSummerPass: false, selectedDayOffsets: [0],
    guardians: [{ name: 'Amos Diggory', relationship: 'Father' }],
    authorizedPickups: [],
    seed: { status: 'not_arrived' },
  },
];

/** How far either side of today the sample programs are made to run. */
const SAMPLE_TERM_DAYS = 120;

/**
 * The sample programs, dated around `today` so they always meet today whichever day the
 * portal is opened — which is the whole point, since sample mode only appears when nothing
 * real is running.
 */
export function sampleProgramsForToday(today: string): TodayProgram[] {
  const start_date = shiftDateString(today, -SAMPLE_TERM_DAYS);
  const end_date = shiftDateString(today, SAMPLE_TERM_DAYS);
  return [
    {
      id: SAMPLE_PROGRAM_IDS.afterSchool,
      name: 'Defence Against the Dark Arts — After School',
      type: 'after-school',
      billing_type: 'recurring_monthly',
      schedule_cron: '00 15 * * 0-6',
      duration: 180,
      start_date,
      end_date,
      // Sample programs meet every day on purpose; nothing to exclude.
      excluded_dates: null,
    },
    {
      id: SAMPLE_PROGRAM_IDS.potions,
      name: 'Potions Club',
      type: 'extracurricular',
      billing_type: 'per_day',
      schedule_cron: '30 15 * * 0-6',
      duration: 45,
      start_date,
      end_date,
      excluded_dates: null,
    },
  ];
}

/** The sample students on one program, in the order the roster RPC would return them. */
export function sampleStudentsForProgram(programId: string): SampleStudent[] {
  return SAMPLE_STUDENTS
    .filter(s => s.programId === programId)
    .slice()
    .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
}

/** Selected `per_day` dates for a student, as real calendar days around today. */
function sampleSelectedDays(student: SampleStudent, today: string): string[] | null {
  if (!student.selectedDayOffsets) return null;
  return student.selectedDayOffsets.map(offset => shiftDateString(today, offset));
}

/**
 * One program's roster in exactly the shape the real page assembles from the
 * `staff_camp_roster` RPC plus the `students.metadata` join, so the roster screen cannot
 * tell the difference.
 */
export interface SampleRosterEntry {
  enrollmentId: string;
  studentId: string;
  studentName: string;
  grade: string | null;
  school: string | null;
  roomNumber: string | null;
  viaSummerPass: boolean;
  hasAllergies: boolean;
  allergiesDetails: string | null;
  selectedDays: string[] | null;
  inAfterSchool: boolean;
}

/**
 * The sample children who hold an after-school seat — derived from the fixtures rather than
 * declared on them, so the demo cannot contradict itself. A child is in after-school here
 * for the same reason a real one is: they have an enrollment in the after-school program.
 *
 * It gives Potions Club a 3-of-4 split, which is the point — the badge is only worth
 * anything on a roster where it differs between rows.
 */
const SAMPLE_AFTER_SCHOOL_STUDENT_IDS: ReadonlySet<string> = new Set(
  SAMPLE_STUDENTS
    .filter(s => s.programId === SAMPLE_PROGRAM_IDS.afterSchool)
    .map(s => s.studentId),
);

export function sampleRosterEntries(programId: string, today: string): SampleRosterEntry[] {
  return sampleStudentsForProgram(programId).map(student => ({
    enrollmentId: student.enrollmentId,
    studentId: student.studentId,
    studentName: `${student.firstName} ${student.lastName}`,
    grade: student.grade,
    school: student.school,
    roomNumber: student.roomNumber,
    viaSummerPass: student.viaSummerPass,
    hasAllergies: student.hasAllergies,
    allergiesDetails: student.allergiesDetails,
    selectedDays: sampleSelectedDays(student, today),
    inAfterSchool: SAMPLE_AFTER_SCHOOL_STUDENT_IDS.has(student.studentId),
  }));
}
